import {
  ArrowPathIcon,
  ArrowRightStartOnRectangleIcon,
  CheckIcon,
  ExclamationTriangleIcon,
} from "@heroicons/react/24/outline";
import { useQueryClient } from "@tanstack/react-query";
import { SUPPORT_FORM_URL } from "@utils/constants";
import { splitMfaAlert } from "@utils/mfaAlert";
import React, { useEffect, useRef, useState } from "react";

import { Paragraph, PopoverPanel } from "@fronterahealth/frontera-ui-components";

import {
  ApiScraperLoginStatusChoices,
  EhrConnectionStatus,
  useDeleteEhrCredentialsMutation,
  useEhrStartWorkflowAndLoginMutation,
} from "@api/graphql/types-and-hooks";
import { Loader } from "@components/ClinicalNewReportDialog/Loader";
import { EHRConnectBadge } from "@components/EHRConnect/EHRConnectBadge";
import { useEhrAnalytics } from "@hooks/useEhrAnalytics";
import { useAdminData } from "@providers/AdminDataProvider";
import { useEHRConnection } from "@providers/EHRConnectionProvider";

const CONNECTED_STATUSES = [
  EhrConnectionStatus.Connected,
  EhrConnectionStatus.ImportingClientList,
  EhrConnectionStatus.ImportingTreatmentPlan,
  EhrConnectionStatus.ExportingTreatmentPlan,
];

export function getEffectiveLoginStatus({
  isCRDataWarehouse,
  isOrgUsernamePassword = false,
  ehrConnectionStatus,
  loginStatus,
  hasCredentials,
}: {
  isCRDataWarehouse: boolean;
  isOrgUsernamePassword?: boolean;
  ehrConnectionStatus: EhrConnectionStatus | null | undefined;
  loginStatus: ApiScraperLoginStatusChoices | null | undefined;
  hasCredentials: boolean;
}): ApiScraperLoginStatusChoices | null | undefined {
  // Org-managed credential modes (data warehouse, org-level admin account) have
  // no per-provider login, so connection state is rendered by dedicated branches
  // in the component rather than inferred from loginStatus here.
  if (isCRDataWarehouse || isOrgUsernamePassword) return loginStatus;
  // CentralReach interactive MFA keeps the connection on LOGGING_IN (never
  // CredentialsFailed) while it holds the browser open on the challenge, so the
  // badge reads "Connecting" throughout — not a failure. Code entry is now in the
  // badge popover (PROA-1674), so LoggingIn is the right effective status here.
  if (ehrConnectionStatus === EhrConnectionStatus.LoggingIn || ehrConnectionStatus === EhrConnectionStatus.Reconnecting)
    return ApiScraperLoginStatusChoices.LoggingIn;
  if (ehrConnectionStatus === EhrConnectionStatus.CredentialsFailed) {
    // MFA and exhausted states are set explicitly by the workflow before CredentialsFailed —
    // preserve them so the popover shows specific messaging rather than "Incorrect Email or Password".
    // LoggingIn / Success are also preserved: while validating a submitted MFA
    // code the connection is still CredentialsFailed but the login is LOGGING_IN
    // ("Connecting…"); right after a successful code the login is Success before
    // the connection mutation lands ("connected") — neither is a credential
    // failure, so don't collapse them to "Incorrect Email or Password".
    if (
      loginStatus === ApiScraperLoginStatusChoices.MfaNotSupported ||
      loginStatus === ApiScraperLoginStatusChoices.MfaRequired ||
      loginStatus === ApiScraperLoginStatusChoices.FailedMfa ||
      loginStatus === ApiScraperLoginStatusChoices.LoginAttemptsExhausted ||
      loginStatus === ApiScraperLoginStatusChoices.LoggingIn ||
      loginStatus === ApiScraperLoginStatusChoices.Success
    )
      return loginStatus;
    return ApiScraperLoginStatusChoices.FailedCreds;
  }
  if (ehrConnectionStatus === EhrConnectionStatus.ConnectionError) return null;
  if (ehrConnectionStatus && CONNECTED_STATUSES.includes(ehrConnectionStatus))
    return ApiScraperLoginStatusChoices.Success;
  // Disconnected is an explicit signal — don't infer Success from stored credentials alone.
  if (!loginStatus && hasCredentials && ehrConnectionStatus !== EhrConnectionStatus.Disconnected)
    return ApiScraperLoginStatusChoices.Success;
  // Disconnected must not show stale Success badge (3-dot menu reads ehrConnectionStatus
  // directly and would show "Connect EHR", creating an inconsistency).
  if (ehrConnectionStatus === EhrConnectionStatus.Disconnected && loginStatus === ApiScraperLoginStatusChoices.Success)
    return null;
  return loginStatus;
}

// Shared style for inline text-action links in the connection popover.
const ACTION_LINK_CLASS = "text-interaction-primary no-underline cursor-pointer";

// Non-admin view of an org-managed (ORG_USERNAME_PASSWORD) EHR connection. The
// connection is managed centrally by an org admin, so this is READ-ONLY — it
// mirrors the shared connection state the backend persists on the org
// credential: verifying (an admin is setting it up), connected, connection
// failed (bad credentials or MFA — both surface as CREDENTIALS_FAILED on the
// wire, so non-admins see a single "Failed EHR connection"), or not yet set up.
// Non-admins can't fix any of these, so every non-connected state points them
// to their administrator.
const OrgManagedConnectBadge: React.FC = () => {
  const { ehrConnectionStatus, isEhrSyncCleared } = useEHRConnection();
  const { captureEhrBadgeClicked } = useEhrAnalytics();

  const isVerifying =
    ehrConnectionStatus === EhrConnectionStatus.LoggingIn || ehrConnectionStatus === EhrConnectionStatus.Reconnecting;
  // A transient CONNECTION_ERROR is treated as connected at the org level: the
  // shared account is set, and a non-admin's own actions auto-reconnect — showing
  // a failure they can't fix would just be noise. (Admins get a retry affordance
  // in OrgAdminConnectBadge.)
  const isConnected =
    ehrConnectionStatus != null &&
    (CONNECTED_STATUSES.includes(ehrConnectionStatus) || ehrConnectionStatus === EhrConnectionStatus.ConnectionError);
  const isFailed = ehrConnectionStatus === EhrConnectionStatus.CredentialsFailed;

  let badge: React.ReactNode;
  let statusLine: React.ReactNode;
  let body: string;
  if (isVerifying) {
    badge = <EHRConnectBadge status={ApiScraperLoginStatusChoices.LoggingIn} isEhrSyncCleared={isEhrSyncCleared} />;
    statusLine = (
      <Paragraph className="flex flex-row gap-0.5">
        <Loader />
        Verifying EHR connection…
      </Paragraph>
    );
    body = "Your organization’s EHR connection is being set up. This can take a moment.";
  } else if (isConnected) {
    badge = <EHRConnectBadge status={ApiScraperLoginStatusChoices.Success} isEhrSyncCleared={isEhrSyncCleared} />;
    statusLine = (
      <Paragraph className="flex flex-row items-center gap-1">
        <CheckIcon className="h-5 w-5 text-green-700" />
        EHR is successfully connected
      </Paragraph>
    );
    body = "Your EHR connection is managed by your organization administrator.";
  } else if (isFailed) {
    badge = <EHRConnectBadge status={ApiScraperLoginStatusChoices.FailedCreds} isEhrSyncCleared={isEhrSyncCleared} />;
    statusLine = (
      <Paragraph className="flex flex-row items-center gap-1">
        <ExclamationTriangleIcon className="h-5 w-5 text-red-500" />
        EHR credentials are no longer valid
      </Paragraph>
    );
    body =
      "Your organization’s EHR credentials are no longer valid. Please contact your organization administrator to restore the connection.";
  } else {
    badge = <EHRConnectBadge status={null} isEhrSyncCleared={isEhrSyncCleared} />;
    statusLine = (
      <Paragraph className="flex flex-row items-center gap-1">
        <ExclamationTriangleIcon className="h-5 w-5 text-yellow-600" />
        EHR is not connected
      </Paragraph>
    );
    body = "Please contact your organization administrator to set up your EHR connection.";
  }

  return (
    <PopoverPanel
      width="w-[450px]"
      height="overflow-hidden!"
      direction="bottom-left"
      content={
        <div className="flex flex-col -m-3 h-full">
          <div className="flex flex-row p-4 border-b justify-between items-center">
            <Paragraph displayType="loud" colorType="primary" size="small">
              EHR Connection Details
            </Paragraph>
          </div>
          <div className="p-4">
            {statusLine}
            <div className="mt-2 p-2 bg-limestone-50 rounded-lg">
              <Paragraph size="extra-small">{body}</Paragraph>
            </div>
          </div>
        </div>
      }
    >
      {/* PopoverPanel wraps this div in a real <button> (Popover.Button), which
      already handles keyboard activation for opening the popover — adding our own
      role="button"/tabIndex/onKeyDown here would nest a second interactive "button"
      role inside that real button (invalid ARIA, and verified to break
      getByRole("button") queries in tests). This onClick is a click-driven analytics
      observer only, not an independent interactive control. */}
      <div // NOSONAR
        onClick={captureEhrBadgeClicked} // NOSONAR
        className="h-6 py-4 px-1.5 rounded-md bg-limestone-50 flex items-center justify-center text-xs text-limestone-600 cursor-pointer" // NOSONAR
      >
        {badge}
      </div>
    </PopoverPanel>
  );
};

const ORG_ADMIN_BADGE_CLASS =
  "h-6 py-4 px-1.5 rounded-md bg-limestone-50 flex items-center justify-center text-xs text-limestone-600";

// Reusable "EHR Connection Details" popover for the org-admin badge: a status
// line (icon + title), a body block, and the badge trigger. Keeps each state
// branch in OrgAdminConnectBadge small.
const OrgAdminStatusPopover: React.FC<{
  badgeStatus: ApiScraperLoginStatusChoices | null;
  isDisconnecting?: boolean;
  isEhrSyncCleared?: boolean;
  statusLine: React.ReactNode;
  children: React.ReactNode;
}> = ({ badgeStatus, isDisconnecting, isEhrSyncCleared, statusLine, children }) => {
  const { captureEhrBadgeClicked } = useEhrAnalytics();
  return (
    <PopoverPanel
      width="w-[450px]"
      height="overflow-hidden!"
      direction="bottom-left"
      content={
        <div className="flex flex-col -m-3 h-full">
          <div className="flex flex-row p-4 border-b justify-between items-center">
            <Paragraph displayType="loud" colorType="primary" size="small">
              EHR Connection Details
            </Paragraph>
          </div>
          <div className="p-4">
            {statusLine}
            {children}
          </div>
        </div>
      }
    >
      {/* PopoverPanel wraps this div in a real <button> (Popover.Button), which
      already handles keyboard activation for opening the popover — adding our own
      role="button"/tabIndex/onKeyDown here would nest a second interactive "button"
      role inside that real button (invalid ARIA, and verified to break
      getByRole("button") queries in tests). This onClick is a click-driven analytics
      observer only, not an independent interactive control. */}
      <div // NOSONAR
        onClick={captureEhrBadgeClicked} // NOSONAR
        className={`${ORG_ADMIN_BADGE_CLASS} cursor-pointer`} // NOSONAR
      >
        <EHRConnectBadge status={badgeStatus} isDisconnecting={isDisconnecting} isEhrSyncCleared={isEhrSyncCleared} />
      </div>
    </PopoverPanel>
  );
};

// Org-level admin: unlike a provider, an org admin *manages* the shared
// credentials from the badge — connect (enter the organization credentials,
// which the backend saves and verifies), watch the verifying → connected /
// invalid / MFA state, and disconnect (which clears the shared credentials and
// ends every provider's active workflow). Connect and disconnect reuse the same
// mutations as the per-provider flow (ehrStartWorkflowAndLogin /
// deleteEhrCredentials) — the backend applies the org-level behavior for admins.
//
// MFA-gated accounts are FLAGGED here (mirroring provider-login messaging) but
// the interactive MFA code dialog is deliberately NOT shown for org-admin setup
// (suppressed in EHRConnectionProvider). An admin resolves an MFA flag by
// connecting an account without MFA.
const OrgAdminConnectBadge: React.FC<{
  onDisconnect: () => void;
  isDisconnecting: boolean;
}> = ({ onDisconnect, isDisconnecting }) => {
  const { ehrConnectionStatus, loginStatus, openEHRLogin, retryConnection, isEhrSyncCleared } = useEHRConnection();
  const { captureEhrBadgeClicked } = useEhrAnalytics();
  const [isRetryPending, setIsRetryPending] = useState(false);

  const disconnectButton = (
    <button
      type="button"
      onClick={onDisconnect}
      disabled={isDisconnecting}
      className="mt-3 flex w-full items-center justify-center gap-1.5 rounded-md border border-limestone-200 px-3 py-1.5 text-xs text-limestone-700 hover:bg-limestone-50 transition-colors"
    >
      <ArrowRightStartOnRectangleIcon className="h-4 w-4" />
      Disconnect
    </button>
  );
  const updateCredentialsLink = (
    <Paragraph size="extra-small" className="mt-2">
      <button className={ACTION_LINK_CLASS} onClick={() => openEHRLogin(undefined, true)}>
        Update credentials
      </button>
    </Paragraph>
  );
  const infoBox = (text: string, extra?: React.ReactNode) => (
    <div className="mt-2 p-2 bg-limestone-50 rounded-lg">
      <Paragraph size="extra-small">{text}</Paragraph>
      {extra}
    </div>
  );
  const failLine = (title: string) => (
    <Paragraph className="flex flex-row items-center gap-1">
      <ExclamationTriangleIcon className="h-5 w-5 text-red-500" />
      {title}
    </Paragraph>
  );

  // "Verifying…" — credentials submitted, the backend test-login is in flight.
  // Used both while actively verifying and as the terminal fallback; keeps a
  // Disconnect escape hatch in case verification stalls.
  const verifyingBadge = (
    <OrgAdminStatusPopover
      badgeStatus={ApiScraperLoginStatusChoices.LoggingIn}
      isDisconnecting={isDisconnecting}
      isEhrSyncCleared={isEhrSyncCleared}
      statusLine={
        <Paragraph className="flex flex-row gap-0.5">
          <Loader />
          Verifying your EHR credentials…
        </Paragraph>
      }
    >
      {infoBox("We’ll let you know once the connection is verified.")}
      {disconnectButton}
    </OrgAdminStatusPopover>
  );

  // Stale login data (e.g. a prior MFA_REQUIRED) must not drive the badge once
  // the org is disconnected / never set up — show the connect affordance then.
  const connectionUnset = ehrConnectionStatus == null || ehrConnectionStatus === EhrConnectionStatus.Disconnected;
  const isConnected = ehrConnectionStatus != null && CONNECTED_STATUSES.includes(ehrConnectionStatus);

  // Disconnected / never set up: a keyboard-accessible badge that opens the
  // credential form. Submitting saves the org credentials and starts the
  // verification login (backend gates the write to admins).
  if (connectionUnset) {
    return (
      <button
        type="button"
        className={`${ORG_ADMIN_BADGE_CLASS} cursor-pointer`}
        onClick={() => {
          captureEhrBadgeClicked();
          openEHRLogin(undefined, true);
        }}
      >
        <EHRConnectBadge status={null} isEhrSyncCleared={isEhrSyncCleared} />
      </button>
    );
  }

  if (isConnected) {
    return (
      <OrgAdminStatusPopover
        badgeStatus={ApiScraperLoginStatusChoices.Success}
        isDisconnecting={isDisconnecting}
        isEhrSyncCleared={isEhrSyncCleared}
        statusLine={
          <Paragraph className="flex flex-row items-center gap-1">
            <CheckIcon className="h-5 w-5 text-green-700" />
            EHR is successfully connected
          </Paragraph>
        }
      >
        {infoBox("Your organization’s EHR connection is active for all providers.")}
        {disconnectButton}
      </OrgAdminStatusPopover>
    );
  }

  // Genuinely verifying/connecting (wire LOGGING_IN / RECONNECTING). A real setup
  // failure moves the wire off these states (→ CONNECTION_ERROR / CREDENTIALS_FAILED),
  // so any loginStatus failure while the wire is still in-progress is stale from a
  // prior attempt — show "Verifying…" rather than the stale failure.
  if (
    ehrConnectionStatus === EhrConnectionStatus.LoggingIn ||
    ehrConnectionStatus === EhrConnectionStatus.Reconnecting
  ) {
    return verifyingBadge;
  }

  // MFA / lockout / hard-failure states live on the per-login status, not the
  // connection status. Flag them (like provider login) — but with no code entry.
  //
  // FAILED_MFA is treated identically to MFA_REQUIRED here: org-level setup never
  // shows a code dialog, so a "failed" MFA is the same actionable situation as a
  // "required" one — the account is MFA-gated and the admin must connect an
  // account without MFA. "The MFA code couldn't be verified" would be misleading
  // (no code was ever entered).
  if (
    loginStatus === ApiScraperLoginStatusChoices.MfaRequired ||
    loginStatus === ApiScraperLoginStatusChoices.FailedMfa
  ) {
    return (
      <OrgAdminStatusPopover
        badgeStatus={ApiScraperLoginStatusChoices.FailedMfa}
        isDisconnecting={isDisconnecting}
        isEhrSyncCleared={isEhrSyncCleared}
        statusLine={failLine("Verification failed")}
      >
        {infoBox(
          "This EHR account has multi-factor authentication enabled, which isn’t supported for organization-level setup yet. Please connect an account without MFA.",
          updateCredentialsLink,
        )}
        {disconnectButton}
      </OrgAdminStatusPopover>
    );
  }

  if (loginStatus === ApiScraperLoginStatusChoices.MfaNotSupported) {
    return (
      <OrgAdminStatusPopover
        badgeStatus={ApiScraperLoginStatusChoices.MfaNotSupported}
        isDisconnecting={isDisconnecting}
        isEhrSyncCleared={isEhrSyncCleared}
        statusLine={failLine("Failed to connect to your EHR")}
      >
        {infoBox(
          "This EHR account has MFA enabled, which can’t be completed automatically. Please connect an account without MFA.",
          updateCredentialsLink,
        )}
        {disconnectButton}
      </OrgAdminStatusPopover>
    );
  }

  if (loginStatus === ApiScraperLoginStatusChoices.LoginAttemptsExhausted) {
    return (
      <OrgAdminStatusPopover
        badgeStatus={ApiScraperLoginStatusChoices.LoginAttemptsExhausted}
        isDisconnecting={isDisconnecting}
        isEhrSyncCleared={isEhrSyncCleared}
        statusLine={failLine("Failed to connect to your EHR")}
      >
        {infoBox("Too many failed login attempts. The account is temporarily locked — please try again later.")}
        {disconnectButton}
      </OrgAdminStatusPopover>
    );
  }

  // Wrong username/password (from the per-login status). During setup the wire is
  // CONNECTION_ERROR (VERIFICATION_FAILED); once ACTIVE it's CREDENTIALS_FAILED —
  // either way the login status says it's a credentials problem, so show the
  // credentials-specific message + Update, not the generic "try again".
  if (loginStatus === ApiScraperLoginStatusChoices.FailedCreds) {
    return (
      <OrgAdminStatusPopover
        badgeStatus={ApiScraperLoginStatusChoices.FailedCreds}
        isDisconnecting={isDisconnecting}
        isEhrSyncCleared={isEhrSyncCleared}
        statusLine={failLine("Incorrect EHR credentials")}
      >
        {infoBox(
          "We couldn’t sign in with the organization credentials provided. Please double-check them and update.",
          updateCredentialsLink,
        )}
        {disconnectButton}
      </OrgAdminStatusPopover>
    );
  }

  // Connection error with no specific login failure — the EHR was unreachable.
  // The credentials may be fine, so let the admin retry the shared connection
  // with the stored credentials (retryConnection — no re-entry needed).
  if (ehrConnectionStatus === EhrConnectionStatus.ConnectionError) {
    return (
      <OrgAdminStatusPopover
        badgeStatus={ApiScraperLoginStatusChoices.FailedCreds}
        isDisconnecting={isDisconnecting}
        isEhrSyncCleared={isEhrSyncCleared}
        statusLine={failLine("Failed to connect to your EHR")}
      >
        {infoBox(
          "We encountered an issue connecting to your EHR. This may be a temporary problem. Please try reconnecting.",
          <Paragraph size="extra-small" className="mt-2">
            <button
              className={`${ACTION_LINK_CLASS} disabled:opacity-50 disabled:cursor-not-allowed`}
              disabled={isRetryPending || isDisconnecting}
              onClick={async () => {
                setIsRetryPending(true);
                try {
                  await retryConnection();
                } finally {
                  setIsRetryPending(false);
                }
              }}
            >
              Try again
            </button>
          </Paragraph>,
        )}
        {disconnectButton}
      </OrgAdminStatusPopover>
    );
  }

  // Fallback: wire says credentials failed but the per-login status didn't
  // resolve to a specific reason (e.g. stale). Treat as a credentials problem.
  if (ehrConnectionStatus === EhrConnectionStatus.CredentialsFailed) {
    return (
      <OrgAdminStatusPopover
        badgeStatus={ApiScraperLoginStatusChoices.FailedCreds}
        isDisconnecting={isDisconnecting}
        isEhrSyncCleared={isEhrSyncCleared}
        statusLine={failLine("EHR credentials are no longer valid")}
      >
        {infoBox(
          "Update your organization’s EHR credentials to restore the connection for all providers.",
          updateCredentialsLink,
        )}
        {disconnectButton}
      </OrgAdminStatusPopover>
    );
  }

  // Fallback: org record is in a setup state with no more specific signal —
  // show "Verifying…" (with the Disconnect escape hatch).
  return verifyingBadge;
};

interface EHRConnectionDetailsBodyProps {
  effectiveLoginStatus: ApiScraperLoginStatusChoices | null | undefined;
  onDisconnect: () => void;
  isDisconnecting: boolean;
}

const MFA_LOGIN_RELAY_MESSAGE = "Login Started";

// Code-entry form rendered directly in the badge popover. Submits the code via
// the same relay mutation the dialog uses; polling surfaces the result (success
// or a retry error in loginAlert) without closing the popover.
const MfaCodeEntry: React.FC<{ loginAlert: string | null | undefined; onSubmitted: () => void }> = ({
  loginAlert,
  onSubmitted,
}) => {
  const { mfaStartedAt, mfaWindowMs } = useEHRConnection();
  const queryClient = useQueryClient();
  const { captureEhrLoginFailed } = useEhrAnalytics();
  const { prompt, error: alertError } = splitMfaAlert(loginAlert);
  const [code, setCode] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [networkError, setNetworkError] = useState<string | null>(null);
  const [timeLeft, setTimeLeft] = useState<number | null>(null);
  const submitMutation = useEhrStartWorkflowAndLoginMutation();

  useEffect(() => {
    if (!mfaStartedAt) {
      setTimeLeft(null);
      return;
    }
    const tick = () => setTimeLeft(Math.max(0, mfaWindowMs - (Date.now() - mfaStartedAt)));
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [mfaStartedAt, mfaWindowMs]);

  const handleSubmit = async () => {
    if (!code.trim() || isSubmitting) return;
    setIsSubmitting(true);
    setNetworkError(null);
    try {
      const data = await submitMutation.mutateAsync({ username: null, password: null, mfa: code.trim() });
      if (data.ehrStartWorkflowAndLogin?.message === MFA_LOGIN_RELAY_MESSAGE) {
        setCode("");
        queryClient.invalidateQueries({ queryKey: ["getEhrLoginStatusQuery"] });
        onSubmitted();
      } else {
        // Same relay-failure reason EHRLoginDialog uses for its own MFA code
        // step — this popover is a second, independent submission path to the
        // same relay mutation, so a failure here is just as real a connect
        // funnel drop-off.
        captureEhrLoginFailed("mfa_relay_error");
        setNetworkError("Could not relay your code. Please try again.");
      }
    } catch {
      captureEhrLoginFailed("mfa_relay_error");
      setNetworkError("Something went wrong. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const inlineError = networkError ?? alertError;
  const expired = timeLeft !== null && timeLeft <= 0;

  const formatTime = (ms: number) => {
    const s = Math.floor(ms / 1000);
    return `${Math.floor(s / 60)}:${(s % 60).toString().padStart(2, "0")}`;
  };

  return (
    <div className="mt-2 p-2 bg-limestone-50 rounded-lg">
      {expired ? (
        <>
          <Paragraph size="extra-small" displayType="loud">
            Code window closed
          </Paragraph>
          <Paragraph size="extra-small">
            The verification window has closed. Please reconnect your EHR to get a new MFA code.
          </Paragraph>
        </>
      ) : (
        <>
          <Paragraph size="extra-small" displayType="loud">
            Enter your verification code
          </Paragraph>
          <Paragraph size="extra-small">{prompt ?? "Enter your MFA code to finish connecting."}</Paragraph>
          {timeLeft !== null && (
            <Paragraph size="extra-small" className="mt-1 text-yellow-700">
              {formatTime(timeLeft)} remaining
            </Paragraph>
          )}
          <input
            type="text"
            inputMode="numeric"
            value={code}
            onChange={(e) => setCode(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                handleSubmit();
              }
            }}
            placeholder="Enter code"
            disabled={isSubmitting}
            className="mt-2 w-full rounded border border-limestone-300 bg-white px-3 py-1.5 text-sm text-text-primary placeholder:text-text-tertiary focus:outline-none focus:ring-1 focus:ring-interaction-primary"
          />
          {/* Plain <p>, not <Paragraph>: Paragraph appends its own colorType class
          (default text-text-secondary) via a non-twMerge joiner, which wins over a
          passed text-red-500 — so the error would not render red. */}
          {inlineError && <p className="mt-1 text-xs text-red-500">{inlineError}</p>}
          <button
            type="button"
            onClick={handleSubmit}
            disabled={!code.trim() || isSubmitting || expired}
            className="mt-2 flex w-full items-center justify-center gap-1.5 rounded-md bg-interaction-primary px-3 py-1.5 text-xs text-white transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {isSubmitting && <ArrowPathIcon className="h-3.5 w-3.5 animate-spin" />}
            {isSubmitting ? "Verifying…" : "Submit"}
          </button>
        </>
      )}
    </div>
  );
};

// Body of the per-provider "EHR Connection Details" popover: renders the single
// status-specific section. Split out from the badge wrapper to keep each
// function's cognitive complexity low.
const EHRConnectionDetailsBody: React.FC<EHRConnectionDetailsBodyProps> = ({
  effectiveLoginStatus,
  onDisconnect,
  isDisconnecting,
}) => {
  const {
    ehrConnectionStatus,
    loginAlert,
    hasCredentials,
    openEHRLogin,
    retryConnection,
    mfaSubmitted,
    setMfaSubmitted,
  } = useEHRConnection();
  const [isRetryPending, setIsRetryPending] = useState(false);

  return (
    <div className="flex flex-col -m-3 h-full">
      <div className="flex flex-row p-4 border-b justify-between items-center">
        <Paragraph displayType="loud" colorType="primary" size="small">
          EHR Connection Details
        </Paragraph>
      </div>
      <div className="p-4">
        {mfaSubmitted ? (
          <div className="p-2 bg-limestone-50 rounded-lg">
            <Paragraph size="extra-small" className="flex flex-row items-center gap-1">
              <Loader />
              Verifying your code…
            </Paragraph>
          </div>
        ) : (
          <>
            {effectiveLoginStatus === ApiScraperLoginStatusChoices.LoggingIn && (
              <>
                <Paragraph className="flex flex-row gap-0.5">
                  <Loader />
                  Connecting to EHR...
                </Paragraph>
                <div className="mt-2 p-2 bg-limestone-50 rounded-lg">
                  <Paragraph size="extra-small">We’ll let you know when your connection is finished.</Paragraph>
                </div>
              </>
            )}
            {effectiveLoginStatus === ApiScraperLoginStatusChoices.Success && (
              <>
                <Paragraph className="flex flex-row items-center gap-1">
                  <CheckIcon className="h-5 w-5 text-green-700" />
                  EHR is successfully connected
                </Paragraph>
                <div className="mt-2 p-2 bg-limestone-50 rounded-lg">
                  <Paragraph size="extra-small">
                    You can import clients and keep your data in sync going forward.
                  </Paragraph>
                </div>
                <button
                  type="button"
                  onClick={onDisconnect}
                  disabled={isDisconnecting}
                  className="mt-3 flex w-full items-center justify-center gap-1.5 rounded-md border border-limestone-200 px-3 py-1.5 text-xs text-limestone-700 hover:bg-limestone-50 transition-colors"
                >
                  <ArrowRightStartOnRectangleIcon className="h-4 w-4" />
                  Disconnect
                </button>
              </>
            )}
            {effectiveLoginStatus === ApiScraperLoginStatusChoices.MfaNotSupported && (
              <>
                <Paragraph className="flex flex-row gap-0.5">
                  <ExclamationTriangleIcon className="h-6 w-6 mr-1 text-red-500" />
                  Failed to connect to your EHR
                </Paragraph>
                <div className="mt-2 p-2 bg-limestone-50 rounded-lg">
                  <Paragraph size="extra-small" displayType="loud">
                    Additional verification required
                  </Paragraph>
                  <Paragraph size="extra-small">
                    Your EHR account appears to have multi-factor authentication enabled. We’re unable to complete the
                    connection while MFA is turned on.
                  </Paragraph>
                  <Paragraph size="extra-small" className="mt-2">
                    Please contact our{" "}
                    <a href={SUPPORT_FORM_URL} target="_blank" rel="noopener noreferrer" className={ACTION_LINK_CLASS}>
                      support team
                    </a>{" "}
                    and we’ll help you get connected.
                  </Paragraph>
                </div>
              </>
            )}
            {effectiveLoginStatus === ApiScraperLoginStatusChoices.MfaRequired && (
              <>
                <Paragraph className="flex flex-row gap-0.5">
                  <ExclamationTriangleIcon className="h-6 w-6 mr-1 text-yellow-600" />
                  Verification required
                </Paragraph>
                <MfaCodeEntry loginAlert={loginAlert} onSubmitted={() => setMfaSubmitted(true)} />
              </>
            )}
            {/* Interactive MFA — failed: the code was rejected (terminal). The user
            must start the connection again, which re-issues the challenge. */}
            {effectiveLoginStatus === ApiScraperLoginStatusChoices.FailedMfa && (
              <>
                <Paragraph className="flex flex-row gap-0.5">
                  <ExclamationTriangleIcon className="h-6 w-6 mr-1 text-red-500" />
                  Verification failed
                </Paragraph>
                <div className="mt-2 p-2 bg-limestone-50 rounded-lg">
                  <Paragraph size="extra-small" displayType="loud">
                    Incorrect MFA code
                  </Paragraph>
                  <Paragraph size="extra-small">
                    The MFA code couldn’t be verified. Please try connecting again.
                  </Paragraph>
                  <Paragraph size="extra-small" className="mt-2">
                    <button className={ACTION_LINK_CLASS} onClick={() => openEHRLogin(undefined, true)}>
                      Try again
                    </button>
                  </Paragraph>
                </div>
                {/* Let the user disconnect and re-connect with different credentials
                (e.g. a different account / a fresh login) rather than only retry. */}
                <button
                  type="button"
                  onClick={onDisconnect}
                  disabled={isDisconnecting}
                  className="mt-3 flex w-full items-center justify-center gap-1.5 rounded-md border border-limestone-200 px-3 py-1.5 text-xs text-limestone-700 hover:bg-limestone-50 transition-colors"
                >
                  <ArrowRightStartOnRectangleIcon className="h-4 w-4" />
                  Disconnect
                </button>
              </>
            )}
            {ehrConnectionStatus === EhrConnectionStatus.ConnectionError && (
              <>
                <Paragraph className="flex flex-row gap-0.5">
                  <ExclamationTriangleIcon className="h-6 w-6 mr-1 text-red-500" />
                  Failed to connect to your EHR
                </Paragraph>
                <div className="mt-2 p-2 bg-limestone-50 rounded-lg">
                  <Paragraph size="extra-small" displayType="loud">
                    Connection issue
                  </Paragraph>
                  <Paragraph size="extra-small">
                    We encountered an issue connecting to your EHR. This may be a temporary problem. Please try
                    reconnecting.
                  </Paragraph>
                  <Paragraph size="extra-small" className="mt-2">
                    <button
                      className={`${ACTION_LINK_CLASS} disabled:opacity-50 disabled:cursor-not-allowed`}
                      disabled={isRetryPending || isDisconnecting}
                      onClick={async () => {
                        if (hasCredentials) {
                          setIsRetryPending(true);
                          try {
                            await retryConnection();
                          } finally {
                            setIsRetryPending(false);
                          }
                        } else {
                          openEHRLogin(undefined, true);
                        }
                      }}
                    >
                      Try again
                    </button>
                  </Paragraph>
                </div>
                <button
                  type="button"
                  onClick={onDisconnect}
                  disabled={isDisconnecting || isRetryPending}
                  className="mt-3 flex w-full items-center justify-center gap-1.5 rounded-md border border-limestone-200 px-3 py-1.5 text-xs text-limestone-700 hover:bg-limestone-50 transition-colors"
                >
                  <ArrowRightStartOnRectangleIcon className="h-4 w-4" />
                  Disconnect
                </button>
              </>
            )}
            {effectiveLoginStatus === ApiScraperLoginStatusChoices.FailedCreds &&
              ehrConnectionStatus !== EhrConnectionStatus.ConnectionError && (
                <>
                  <Paragraph className="flex flex-row gap-0.5">
                    <ExclamationTriangleIcon className="h-6 w-6 mr-1 text-red-500" />
                    Failed to connect to your EHR
                  </Paragraph>
                  <div className="mt-2 p-2 bg-limestone-50 rounded-lg">
                    {loginAlert ? (
                      // EHR-specific failure (e.g. Motivity multi-org / no-org):
                      // show the backend message verbatim instead of the generic
                      // wrong-credentials copy.
                      <Paragraph size="extra-small">{loginAlert}</Paragraph>
                    ) : (
                      <>
                        <Paragraph size="extra-small" displayType="loud">
                          Incorrect Email or Password
                        </Paragraph>
                        <Paragraph size="extra-small">
                          We couldn’t sign in to your EHR with the credentials provided. Please double-check your email
                          and password and try again.
                        </Paragraph>
                      </>
                    )}
                    <Paragraph size="extra-small" className="mt-2">
                      <button className={ACTION_LINK_CLASS} onClick={() => openEHRLogin(undefined, true)}>
                        Retry login
                      </button>
                    </Paragraph>
                  </div>
                </>
              )}
            {effectiveLoginStatus === ApiScraperLoginStatusChoices.LoginAttemptsExhausted &&
              ehrConnectionStatus !== EhrConnectionStatus.ConnectionError && (
                <>
                  <Paragraph className="flex flex-row gap-0.5">
                    <ExclamationTriangleIcon className="h-6 w-6 mr-1 text-red-500" />
                    Failed to connect to your EHR
                  </Paragraph>
                  <div className="mt-2 p-2 bg-limestone-50 rounded-lg">
                    {loginAlert ? (
                      // EHR-specific lockout copy (e.g. Rethink: reset your
                      // password): show the backend message verbatim instead of
                      // the generic "try again later" copy, which is misleading
                      // when the account won't auto-unlock.
                      <Paragraph size="extra-small">{loginAlert}</Paragraph>
                    ) : (
                      <>
                        <Paragraph size="extra-small" displayType="loud">
                          Too many failed attempts
                        </Paragraph>
                        <Paragraph size="extra-small">
                          Your account has been temporarily locked due to too many failed login attempts. Please try
                          again later.
                        </Paragraph>
                      </>
                    )}
                  </div>
                </>
              )}
          </>
        )}
      </div>
    </div>
  );
};

interface EHRProviderConnectBadgeProps {
  effectiveLoginStatus: ApiScraperLoginStatusChoices | null | undefined;
  onDisconnect: () => void;
  isDisconnecting: boolean;
}

// Per-provider EHR badge + connection-details popover. Reached only after the
// data-warehouse / org-managed branches are ruled out, so credential entry and
// retry are available here.
const EHRProviderConnectBadge: React.FC<EHRProviderConnectBadgeProps> = ({
  effectiveLoginStatus,
  onDisconnect,
  isDisconnecting,
}) => {
  const { ehrConnectionStatus, hasCredentials, openEHRLogin, registerOpenMfaPopover, isEhrSyncCleared } =
    useEHRConnection();
  const { captureEhrBadgeClicked } = useEhrAnalytics();
  // Ref on the popover trigger div — clicked programmatically when the global
  // MFA strip's "Enter code" CTA is pressed, so both paths open the same popover.
  const badgeTriggerRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    registerOpenMfaPopover(() => {
      badgeTriggerRef.current?.click();
      // Blur immediately after the click so the layout tooltip (which triggers on
      // focus-bubble) doesn't pop up when the strip CTA opens the popover.
      requestAnimationFrame(() => (document.activeElement as HTMLElement | null)?.blur());
    });
  }, [registerOpenMfaPopover]);

  // CR DW / org-managed modes are excluded before this component renders, so a
  // missing login status with no stored credentials means "ready to connect".
  const canOpenLogin = !effectiveLoginStatus && !hasCredentials;
  const shouldShowPopover =
    ehrConnectionStatus === EhrConnectionStatus.ConnectionError ||
    (effectiveLoginStatus &&
      [
        ApiScraperLoginStatusChoices.LoggingIn,
        ApiScraperLoginStatusChoices.Success,
        ApiScraperLoginStatusChoices.MfaNotSupported,
        ApiScraperLoginStatusChoices.MfaRequired,
        ApiScraperLoginStatusChoices.FailedMfa,
        ApiScraperLoginStatusChoices.FailedCreds,
        ApiScraperLoginStatusChoices.LoginAttemptsExhausted,
      ].includes(effectiveLoginStatus));

  if (!shouldShowPopover) {
    const badgeClassName =
      "h-6 py-4 px-1.5 rounded-md bg-limestone-50 flex items-center justify-center text-xs text-text-primary";
    // Use a native <button> when the badge opens login (keyboard-accessible by
    // default); otherwise render a plain, non-interactive <div>.
    if (canOpenLogin) {
      return (
        <button
          type="button"
          className={`${badgeClassName} cursor-pointer`}
          onClick={() => {
            captureEhrBadgeClicked();
            openEHRLogin(undefined, true);
          }}
        >
          <EHRConnectBadge status={effectiveLoginStatus} isEhrSyncCleared={isEhrSyncCleared} />
        </button>
      );
    }
    return (
      <div className={badgeClassName}>
        <EHRConnectBadge status={effectiveLoginStatus} isEhrSyncCleared={isEhrSyncCleared} />
      </div>
    );
  }

  return (
    <PopoverPanel
      width="w-[450px]"
      height="overflow-hidden!"
      direction="bottom-left"
      content={
        <EHRConnectionDetailsBody
          effectiveLoginStatus={effectiveLoginStatus}
          onDisconnect={onDisconnect}
          isDisconnecting={isDisconnecting}
        />
      }
    >
      {/* PopoverPanel wraps this div in a real <button> (Popover.Button), which
      already handles keyboard activation for opening the popover — adding our own
      role="button"/tabIndex/onKeyDown here would nest a second interactive "button"
      role inside that real button (invalid ARIA, and verified to break
      getByRole("button") queries in tests). This onClick is a click-driven analytics
      observer only, not an independent interactive control. */}
      <div // NOSONAR
        ref={badgeTriggerRef}
        // The MFA strip's "Enter code" CTA opens this same popover by calling
        // badgeTriggerRef.current.click() programmatically — isTrusted excludes
        // that synthetic click so it isn't miscounted as a real badge click.
        onClick={(e) => {
          if (e.nativeEvent.isTrusted) captureEhrBadgeClicked(); // NOSONAR
        }}
        className="h-6 py-4 px-1.5 rounded-md bg-limestone-50 flex items-center justify-center text-xs text-text-primary cursor-pointer" // NOSONAR
      >
        <EHRConnectBadge
          status={effectiveLoginStatus ?? null}
          isConnectionError={!effectiveLoginStatus && ehrConnectionStatus === EhrConnectionStatus.ConnectionError}
          isDisconnecting={isDisconnecting}
          isEhrSyncCleared={isEhrSyncCleared}
        />
      </div>
    </PopoverPanel>
  );
};

interface EHRConnectProps {}
export const EHRConnect: React.FC<EHRConnectProps> = ({}) => {
  const {
    isEHREnabled,
    isEHRStatusLoading,
    // Provider always supplies a concrete boolean (Boolean(orgDetails?.ehrSyncCleared));
    // default true mirrors EHRConnectBadge and only guards the never-in-prod undefined.
    isEhrSyncCleared = true,
    loginStatus,
    ehrConnectionStatus,
    isCRDataWarehouse,
    isOrgUsernamePassword,
    hasCredentials,
    mfaStartedAt,
    mfaSubmitted,
  } = useEHRConnection();
  const { roles } = useAdminData();
  const { captureEhrDisconnected, captureEhrBadgeClicked, captureEhrDisconnectClicked } = useEhrAnalytics();
  // Org admins manage the shared org-level credentials (add / verify /
  // disconnect) themselves; every other user in an org-managed org is directed
  // to their administrator. Matches the backend admin gate on the reused
  // ehrStartWorkflowAndLogin / deleteEhrCredentials mutations.
  const isOrgAdmin = (roles ?? []).includes("admin");
  const queryClient = useQueryClient();
  const deleteEhrCredentialsMutation = useDeleteEhrCredentialsMutation({
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["getHasEhrCredentials"] });
      queryClient.invalidateQueries({ queryKey: ["ehrConnection"] });
      queryClient.invalidateQueries({ queryKey: ["getEhrLoginStatusQuery"] });
      captureEhrDisconnected();
    },
  });
  const handleDisconnect = () => {
    // Managed-onboarding gate: the backend now restricts this mutation to
    // frontera-internal while the org isn't cleared for sync — matches that
    // restriction here rather than surfacing its rejection as a raw error.
    if (!isEhrSyncCleared) return;
    captureEhrDisconnectClicked();
    deleteEhrCredentialsMutation.mutate({});
  };

  if (!isEHREnabled) return null;

  if (isEHRStatusLoading) {
    return (
      <div className="h-6 py-4 px-1.5 rounded-md bg-limestone-50 flex items-center justify-center text-xs text-text-primary">
        <div className="h-4 w-24 animate-pulse rounded-sm bg-limestone-200" />
      </div>
    );
  }

  // Managed-onboarding gate: while the org isn't cleared for EHR sync, the whole
  // header EHR surface shows one neutral "setting up" state — badge AND popover —
  // regardless of the underlying connection status. Short-circuits every
  // connection-state branch below so the popover can never contradict the badge
  // (e.g. "EHR is successfully connected" + Disconnect under a "Setting up your
  // connection" badge). The backend blocks the same actions server-side.
  if (!isEhrSyncCleared) {
    return (
      <PopoverPanel
        width="w-[450px]"
        height="overflow-hidden!"
        direction="bottom-left"
        content={
          <div className="flex flex-col -m-3 h-full">
            <div className="flex flex-row p-4 border-b justify-between items-center">
              <Paragraph displayType="loud" colorType="primary" size="small">
                EHR Connection Details
              </Paragraph>
            </div>
            <div className="p-4">
              <div className="p-2 bg-limestone-50 rounded-lg">
                <Paragraph size="extra-small">
                  Frontera is still setting up your EHR connection.{" "}
                  <a href={SUPPORT_FORM_URL} target="_blank" rel="noopener noreferrer" className={ACTION_LINK_CLASS}>
                    Contact support
                  </a>{" "}
                  to get started.
                </Paragraph>
              </div>
            </div>
          </div>
        }
      >
        <div className="h-6 py-4 px-1.5 rounded-md bg-limestone-50 flex items-center justify-center text-xs text-text-primary cursor-pointer">
          <EHRConnectBadge status={null} isEhrSyncCleared={false} />
        </div>
      </PopoverPanel>
    );
  }

  // CR DW orgs show "connected" badge for any status where the connection is established or in progress
  const isCRDWConnected =
    isCRDataWarehouse && ehrConnectionStatus != null && CONNECTED_STATUSES.includes(ehrConnectionStatus);

  // Use optimistic ehrConnectionStatus to derive loginStatus when backend hasn't caught up yet
  const effectiveLoginStatus = getEffectiveLoginStatus({
    isCRDataWarehouse,
    isOrgUsernamePassword,
    ehrConnectionStatus,
    loginStatus,
    hasCredentials,
  });
  // During an active MFA session some EHRs (e.g. CentralReach) hold the connection
  // on LOGGING_IN throughout, so effectiveLoginStatus resolves to LoggingIn while the
  // user is still being asked for a code. Pin the badge to "Verification required"
  // only while we are genuinely awaiting the code: the backend still reports
  // MFA_REQUIRED AND no code is in flight (mfaSubmitted).
  //
  // The login status is the reliable signal: it flips MFA_REQUIRED → LOGGING_IN once
  // the code is submitted, while ehr_connection stays LOGGING_IN throughout. So while
  // the login is still MFA_REQUIRED (and no code is in flight) the badge pins to
  // "Verification required" and the popover surfaces the code form; once it flips to
  // LOGGING_IN (or the code is submitted) the badge shows "Connecting to EHR". A wrong
  // code re-issues MFA_REQUIRED, returning the badge to "Verification required".
  const isAwaitingMfaCode = loginStatus === ApiScraperLoginStatusChoices.MfaRequired && !mfaSubmitted;
  const displayLoginStatus: typeof effectiveLoginStatus =
    effectiveLoginStatus === ApiScraperLoginStatusChoices.LoggingIn && mfaStartedAt !== null && isAwaitingMfaCode
      ? ApiScraperLoginStatusChoices.MfaRequired
      : effectiveLoginStatus;

  // CR DW connected: show popover with disconnect option
  if (isCRDWConnected) {
    return (
      <PopoverPanel
        width="w-[450px]"
        height="overflow-hidden!"
        direction="bottom-left"
        content={
          <div className="flex flex-col -m-3 h-full">
            <div className="flex flex-row p-4 border-b justify-between items-center">
              <Paragraph displayType="loud" colorType="primary" size="small">
                EHR Connection Details
              </Paragraph>
            </div>
            <div className="p-4">
              <Paragraph className="flex flex-row items-center gap-1">
                <CheckIcon className="h-5 w-5 text-green-700" />
                EHR is successfully connected
              </Paragraph>
              <div className="mt-2 p-2 bg-limestone-50 rounded-lg">
                <Paragraph size="extra-small">
                  You can import clients and keep your data in sync going forward.
                </Paragraph>
              </div>
              <button
                type="button"
                onClick={handleDisconnect}
                disabled={deleteEhrCredentialsMutation.isPending}
                className="mt-3 flex w-full items-center justify-center gap-1.5 rounded-md border border-limestone-200 px-3 py-1.5 text-xs text-limestone-700 hover:bg-limestone-50 transition-colors"
              >
                <ArrowRightStartOnRectangleIcon className="h-4 w-4" />
                Disconnect
              </button>
            </div>
          </div>
        }
      >
        {/* PopoverPanel wraps this div in a real <button> (Popover.Button), which
        already handles keyboard activation for opening the popover — adding our own
        role="button"/tabIndex/onKeyDown here would nest a second interactive "button"
        role inside that real button (invalid ARIA, and verified to break
        getByRole("button") queries in tests). This onClick is a click-driven analytics
        observer only, not an independent interactive control. */}
        <div // NOSONAR
          onClick={captureEhrBadgeClicked} // NOSONAR
          className="h-6 py-4 px-1.5 rounded-md bg-limestone-50 flex items-center justify-center text-xs text-text-primary cursor-pointer" // NOSONAR
        >
          <EHRConnectBadge
            status={ApiScraperLoginStatusChoices.Success}
            isDisconnecting={deleteEhrCredentialsMutation.isPending}
            isEhrSyncCleared={isEhrSyncCleared}
          />
        </div>
      </PopoverPanel>
    );
  }

  // CR DW disconnected: show popover directing user to contact their admin
  if (isCRDataWarehouse) {
    return (
      <PopoverPanel
        width="w-[450px]"
        height="overflow-hidden!"
        direction="bottom-left"
        content={
          <div className="flex flex-col -m-3 h-full">
            <div className="flex flex-row p-4 border-b justify-between items-center">
              <Paragraph displayType="loud" colorType="primary" size="small">
                EHR Connection Details
              </Paragraph>
            </div>
            <div className="p-4">
              <Paragraph className="flex flex-row items-center gap-1">
                <ExclamationTriangleIcon className="h-5 w-5 text-yellow-600" />
                EHR is not connected
              </Paragraph>
              <div className="mt-2 p-2 bg-limestone-50 rounded-lg">
                <Paragraph size="extra-small">
                  Please contact your organization administrator to configure EHR credentials.
                </Paragraph>
              </div>
            </div>
          </div>
        }
      >
        {/* PopoverPanel wraps this div in a real <button> (Popover.Button), which
        already handles keyboard activation for opening the popover — adding our own
        role="button"/tabIndex/onKeyDown here would nest a second interactive "button"
        role inside that real button (invalid ARIA, and verified to break
        getByRole("button") queries in tests). This onClick is a click-driven analytics
        observer only, not an independent interactive control. */}
        <div // NOSONAR
          onClick={captureEhrBadgeClicked} // NOSONAR
          className="h-6 py-4 px-1.5 rounded-md bg-limestone-50 flex items-center justify-center text-xs text-text-primary cursor-pointer" // NOSONAR
        >
          <EHRConnectBadge status={null} isEhrSyncCleared={isEhrSyncCleared} />
        </div>
      </PopoverPanel>
    );
  }

  // Org-level admin: manage the shared credentials directly (connect / verify /
  // disconnect) across every connection state. Non-admins fall through to the
  // read-only "managed by your administrator" branches below.
  if (isOrgUsernamePassword && isOrgAdmin) {
    return (
      <OrgAdminConnectBadge onDisconnect={handleDisconnect} isDisconnecting={deleteEhrCredentialsMutation.isPending} />
    );
  }

  // Org-level (non-admin): a single read-only badge reflects the shared
  // connection state (verifying / connected / verification failed / not set up).
  if (isOrgUsernamePassword) {
    return <OrgManagedConnectBadge />;
  }

  return (
    <EHRProviderConnectBadge
      effectiveLoginStatus={displayLoginStatus}
      onDisconnect={handleDisconnect}
      isDisconnecting={deleteEhrCredentialsMutation.isPending}
    />
  );
};
